import React, { useState } from 'react';
import { useFetch } from '../../hooks/useFetch';
import { Spinner, ErrorBanner } from '../../components/ui';
import { fmt } from '../../utils/format';

const channelLabel = {
  shopify: 'Shopify',
  amazon: 'Amazon',
  wintron: 'Wintron',
  quickbooks: 'QuickBooks',
};

const statusColor = {
  success: 'var(--color-success)',
  partial: 'var(--color-warning)',
  error: 'var(--color-danger)',
};

export default function SyncLog({ onClose }) {
  const [channel, setChannel] = useState('');
  const [eventType, setEventType] = useState(''); // '' = both, 'sync', 'write'
  const [expanded, setExpanded] = useState(null);
  const { data, loading, error, refetch } = useFetch(
    `/api/integrations/sync-log?limit=200${channel ? `&channel=${channel}` : ''}${eventType ? `&eventType=${eventType}` : ''}`
  );

  const rows = data || [];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button className="btn btn-sm" onClick={onClose}><i className="ti ti-arrow-left" /> Back</button>
        <div>
          <h2 style={{ margin: 0, fontSize: 18 }}>Sync Log</h2>
          <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>Last 200 sync events</div>
        </div>
        <div style={{ flex: 1 }} />
        <select value={channel} onChange={e => setChannel(e.target.value)} style={{ width: 160 }}>
          <option value="">All channels</option>
          <option value="shopify">Shopify sync</option>
          <option value="shopify_write">Shopify writes</option>
          <option value="wintron">Wintron</option>
          <option value="third_party">Third party</option>
          <option value="amazon">Amazon</option>
        </select>
        <select value={eventType} onChange={e => setEventType(e.target.value)} style={{ width: 140 }}>
          <option value="">Syncs + Writes</option>
          <option value="sync">Syncs only</option>
          <option value="write">Writes only</option>
        </select>
        <button className="btn btn-sm" onClick={refetch}><i className="ti ti-refresh" /> Refresh</button>
      </div>

      {error && <ErrorBanner message={error} />}
      {loading && <Spinner />}

      {!loading && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Channel</th>
                <th>Type</th>
                <th>Store</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Processed</th>
                <th style={{ textAlign: 'right' }}>Matched</th>
                <th style={{ textAlign: 'right' }}>Skipped</th>
                <th>Duration</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {!rows.length && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 24, color: 'var(--text-tertiary)' }}>No sync events recorded yet</td></tr>
              )}
              {rows.map(row => {
                const duration = row.finished_at && row.started_at
                  ? Math.round((new Date(row.finished_at) - new Date(row.started_at)) / 1000)
                  : null;
                const hasErrors = row.details?.errors?.length > 0;
                return (
                  <React.Fragment key={row.id}>
                    <tr
                      onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                      style={{ cursor: hasErrors ? 'pointer' : 'default' }}
                    >
                      <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmt.dateTime(row.started_at)}</td>
                      <td><span className="badge badge-gray" style={{ fontSize: 10 }}>{channelLabel[row.channel] || row.channel}</span></td>
                      <td><span className={`badge ${row.event_type === 'write' ? 'badge-blue' : 'badge-gray'}`} style={{ fontSize: 10 }}>{row.event_type || 'sync'}</span></td>
                      <td style={{ fontSize: 12 }}>{row.store_name || '—'}</td>
                      <td>
                        <span style={{ fontWeight: 600, color: statusColor[row.status] || 'var(--text-primary)', fontSize: 12 }}>
                          {row.status}
                          {hasErrors && <i className="ti ti-chevron-down" style={{ marginLeft: 4, fontSize: 10 }} />}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right', fontSize: 12 }}>{row.records_processed ?? '—'}</td>
                      <td style={{ textAlign: 'right', fontSize: 12 }}>{row.records_matched ?? '—'}</td>
                      <td style={{ textAlign: 'right', fontSize: 12 }}>{row.records_skipped ?? '—'}</td>
                      <td style={{ fontSize: 12 }}>{duration != null ? `${duration}s` : '—'}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-tertiary)', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.message}
                      </td>
                    </tr>
                    {expanded === row.id && hasErrors && (
                      <tr>
                        <td colSpan={9} style={{ padding: '8px 16px', background: 'var(--red-light)' }}>
                          <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6 }}>Errors ({row.details.errors.length}):</div>
                          {row.details.errors.map((e, i) => (
                            <div key={i} style={{ fontSize: 12, marginBottom: 4 }}>
                              <strong>{e.sku}</strong>: {e.error}
                            </div>
                          ))}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
